import { useState, useEffect, useCallback } from 'react';

export const useDeviceSelection = () => {
  const [videoInputDevices, setVideoInputDevices] = useState([]);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState('');
  const [deviceError, setDeviceError] = useState(null);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  // Request permissions first before enumerating devices
  const requestPermissions = useCallback(async () => {
    if (isRequestingPermissions) return;
    
    setIsRequestingPermissions(true);
    setDeviceError(null);
    
    try {
      console.log('Requesting camera and microphone permissions...');
      
      // Request both video and audio permissions
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      console.log('Permissions granted successfully');
      setPermissionsGranted(true);
      
      // Stop the stream immediately as we only needed it for permissions
      stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (err) {
      console.error('Permission request failed:', err);
      
      let errorMessage = 'Camera and microphone access is required for video calls.';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera and microphone access was denied. Please allow access in your browser settings and refresh the page.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera or microphone found. Please connect your devices and refresh the page.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera or microphone is being used by another application. Please close other apps and try again.';
      } else if (err.name === 'SecurityError') {
        errorMessage = 'Access to camera and microphone is blocked by security settings. Please check your browser security settings.';
      }
      
      setDeviceError(errorMessage);
      setPermissionsGranted(false);
      return false;
    } finally {
      setIsRequestingPermissions(false);
    }
  }, [isRequestingPermissions]);

  const enumerateDevices = useCallback(async () => {
    if (!permissionsGranted) {
      console.log('Permissions not granted yet, skipping device enumeration');
      return;
    }
    
    try {
      console.log('Enumerating devices...');
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      const audioDevices = devices.filter(d => d.kind === 'audioinput');
      
      console.log('Found devices:', {
        video: videoDevices.length,
        audio: audioDevices.length
      });
      
      setVideoInputDevices(videoDevices);
      setAudioInputDevices(audioDevices);
      
      // Set default selected devices if not already set
      if (videoDevices.length && !selectedVideoDeviceId) {
        setSelectedVideoDeviceId(videoDevices[0].deviceId);
        console.log('Selected default video device:', videoDevices[0].label || videoDevices[0].deviceId);
      }
      if (audioDevices.length && !selectedAudioDeviceId) {
        setSelectedAudioDeviceId(audioDevices[0].deviceId);
        console.log('Selected default audio device:', audioDevices[0].label || audioDevices[0].deviceId);
      }
      
      setDeviceError(null);
    } catch (err) {
      console.error('Error enumerating devices:', err);
      setDeviceError('Unable to access camera/microphone devices');
    }
  }, [permissionsGranted, selectedVideoDeviceId, selectedAudioDeviceId]);

  // Check if permissions are already granted (useful for page refreshes)
  const checkExistingPermissions = useCallback(async () => {
    try {
      if (!navigator.permissions || !navigator.permissions.query) {
        // Fallback: try to enumerate devices to see if we have access
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasLabels = devices.some(device => device.label && device.label.trim() !== '');
        
        if (hasLabels) {
          console.log('Detected existing permissions (via device labels)');
          setPermissionsGranted(true);
          return true;
        }
        return false;
      }
      
      const cameraPermission = await navigator.permissions.query({ name: 'camera' });
      const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
      
      const hasPermissions = cameraPermission.state === 'granted' && microphonePermission.state === 'granted';
      
      console.log('Permission states:', {
        camera: cameraPermission.state,
        microphone: microphonePermission.state,
        granted: hasPermissions
      });
      
      setPermissionsGranted(hasPermissions);
      return hasPermissions;
    } catch (err) {
      console.log('Could not check existing permissions:', err);
      return false;
    }
  }, []);

  // Initialize permissions and devices on mount
  useEffect(() => {
    const initialize = async () => {
      console.log('Initializing device selection...');
      
      // First check if we already have permissions
      const hasExistingPermissions = await checkExistingPermissions();
      
      if (hasExistingPermissions) {
        // We already have permissions, enumerate devices
        await enumerateDevices();
      } else {
        // No permissions yet, will need to request them
        console.log('No existing permissions detected');
      }
    };
    
    initialize();
  }, [checkExistingPermissions, enumerateDevices]);

  // Enumerate devices when permissions are granted
  useEffect(() => {
    if (permissionsGranted) {
      enumerateDevices();
    }
  }, [permissionsGranted, enumerateDevices]);

  const handleVideoDeviceChange = useCallback((deviceId, onDeviceChange) => {
    setSelectedVideoDeviceId(deviceId);
    if (onDeviceChange) {
      onDeviceChange(deviceId);
    }
  }, []);

  const handleAudioDeviceChange = useCallback((deviceId, onDeviceChange) => {
    setSelectedAudioDeviceId(deviceId);
    if (onDeviceChange) {
      onDeviceChange(deviceId);
    }
  }, []);

  const refreshDevices = useCallback(async () => {
    if (!permissionsGranted) {
      const granted = await requestPermissions();
      if (granted) {
        await enumerateDevices();
      }
    } else {
      await enumerateDevices();
    }
  }, [permissionsGranted, requestPermissions, enumerateDevices]);

  const getDeviceLabel = useCallback((device, defaultPrefix = 'Device') => {
    if (device.label && device.label.trim() !== '') {
      return device.label;
    }
    // Generate a user-friendly name when label is not available
    const deviceIndex = device.kind === 'videoinput' 
      ? videoInputDevices.findIndex(d => d.deviceId === device.deviceId) + 1
      : audioInputDevices.findIndex(d => d.deviceId === device.deviceId) + 1;
    
    return `${defaultPrefix} ${deviceIndex}`;
  }, [videoInputDevices, audioInputDevices]);

  return {
    videoInputDevices,
    audioInputDevices,
    selectedVideoDeviceId,
    selectedAudioDeviceId,
    deviceError,
    permissionsGranted,
    isRequestingPermissions,
    setSelectedVideoDeviceId,
    setSelectedAudioDeviceId,
    handleVideoDeviceChange,
    handleAudioDeviceChange,
    refreshDevices,
    requestPermissions,
    getDeviceLabel,
    enumerateDevices
  };
}; 